import { useCurrentWorkspaceContext } from "@/domain/workspace/hooks/useCurrentWorkspaceContext";
import { DeleteUserFavoriteMutation } from "@/graphql/generated/graphql";
import { useMutation } from "@apollo/client";

import { EVENT_USER_FAVORITE_DELETED, trackUserFavoriteEvent } from "./tracking";
import { deleteUserFavoriteMutationDocument, userFavoritesQueryDocument } from "./user-favorite.gql";

export type DeletedUserFavorite = DeleteUserFavoriteMutation["deleteUserFavorite"];

export interface UseDeleteUserFavoriteProps {
  onCompleted?: (deletedUserFavorite: DeletedUserFavorite) => void;
  onError?: (error: Error) => void;
}

export const useDeleteUserFavorite = ({ onCompleted, onError }: UseDeleteUserFavoriteProps = {}) => {
  const { currentWorkspace } = useCurrentWorkspaceContext();
  const [deleteFavorite, { loading, error }] = useMutation(deleteUserFavoriteMutationDocument, {
    update: (_cache, { data }) => {
      const userFavorite = data?.deleteUserFavorite;
      if (!userFavorite) {
        return;
      }
      trackUserFavoriteEvent(EVENT_USER_FAVORITE_DELETED, {
        userFavoriteId: userFavorite.id,
      });
    },
    onCompleted: onDeleteCompleted,
    onError: onDeleteError,
    refetchQueries: [
      {
        query: userFavoritesQueryDocument,
        variables: { getUserFavoriteListInput: { workspaceId: currentWorkspace?.id } },
      },
    ],
  });

  function onDeleteCompleted(data: DeleteUserFavoriteMutation) {
    if (onCompleted) {
      onCompleted(data.deleteUserFavorite);
    }
  }

  function onDeleteError(error: Error) {
    if (onError) {
      onError(error);
    }
  }

  return {
    deleteFavorite,
    loading,
    error,
  };
};
